import { isStylableLineSymbol, type LineSymbol, type LineSymbolShape } from '@ikegps/core-api';
import { ColorPicker } from '../../components/color.picker/color.picker';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { LineSwatch } from '../../components/line.swatch';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, PREVIEW_HEIGHT, PREVIEW_WIDTH, Style } from './line.symbol.customizer.style';

addStyles(Style);

export interface LineSymbolCustomizerAttrs {
	symbol: LineSymbol;
	/** Stroke shapes offered in the style grid (the freely-stylable ones). */
	shapes: readonly LineSymbolShape[];
	palette: readonly string[];
	/** Apply a new stroke shape, keeping the current color. */
	onPickShape: (symbolId: string) => void;
	/** Apply a new color, keeping the current shape. */
	onPickColor: (color: string) => void;
}

/** Presentational — pure props-in / callbacks-out, no observable state, so it ports cleanly to a React component later. */
export const LineSymbolCustomizer = {
	view(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
		return m('div', { className: CSS.container }, [
			viewPreview(attrs.symbol),
			viewStyleSection(attrs),
			viewColorSection(attrs),
		]);
	},
};

function viewPreview(symbol: LineSymbol): MithrilVNode {
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_LINE_PREVIEW)),
		m('div', { className: CSS.preview }, LineSwatch.view({ symbol, width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT })),
	]);
}

function viewStyleSection(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	// A symbol with an intrinsic glyph (utility code label / fence-railroad marker) is color-only.
	const stylable = isStylableLineSymbol(attrs.symbol.id);
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_LINE_STYLE)),
		stylable ? viewShapeGrid(attrs) : viewFixedMessage(attrs.symbol),
	]);
}

function viewFixedMessage(symbol: LineSymbol): MithrilVNode {
	const message =
		symbol.label != null
			? Translate(T.FIELD_LINE_STYLE_FIXED_CODE, { label: symbol.label })
			: Translate(T.FIELD_LINE_STYLE_FIXED_MARKER);
	return m('div', { className: CSS.fixedMessage }, [Icon.view(MaterialIcon.LOCK), m('span', message)]);
}

function viewShapeGrid(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	const { symbol, shapes, onPickShape } = attrs;
	return m(
		'div',
		{ className: CSS.shapeGrid },
		shapes.map((shape) => {
			const active = shape.id === symbol.id;
			return m(
				'button',
				{
					type: 'button',
					className: active ? `${CSS.chip} ${CSS.chipActive}` : CSS.chip,
					title: shape.name,
					onclick: () => onPickShape(shape.id),
				},
				[
					m(
						'div',
						{ className: CSS.chipSwatch },
						LineSwatch.view({ symbol: { ...shape, color: symbol.color }, width: 120, height: 14 })
					),
					m('div', { className: CSS.chipLabel }, shape.name),
				]
			);
		})
	);
}

function viewColorSection(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	const { symbol, palette, onPickColor } = attrs;
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.COLOR)),
		ColorPicker.view({ color: symbol.color, palette, onPickColor }),
	]);
}
